import { useToast } from '@chakra-ui/react';
import { NotificationCategory } from '@pelicargo/types';
import { uniqBy } from 'lodash';
import { useMemo } from 'react';

import { SimpleCustomerRecipientForm } from '../../components/SimpleCustomerRecipientForm';
import { SimpleForm } from '../../components/SimpleForm';
import { trpc } from '../../config/trpc';
import { useCustomerQuoteUpdateForm } from '../../hooks/emails/useCustomerQuoteUpdateForm';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { useEmail } from '../../hooks/useEmail';
import { hasCustomerEmail } from '../../utils/email';
import { EmailDrawer } from '../Emails/EmailDrawer';
import { EmailDrawerLeftPanel } from '../Emails/EmailDrawerLeftPanel';
import { EmailDrawerRightPanel } from '../Emails/EmailDrawerRightPanel';
import { EmailQuoteForm } from '../Emails/EmailQuoteForm';
import { useRequestDrawersContext } from '../RequestDrawers';

export const NewRateDrawer = () => {
  const toast = useToast();
  const { request } = useCurrentRequest();
  const { methods } = useCustomerQuoteUpdateForm(request);
  const { data: contacts } = trpc.getUsersContacts.useQuery(
    { ids: [request?.default_sender_contact?.user?.id] },
    { enabled: !!request?.default_sender_contact?.user?.id },
  );
  const { data: defaultContacts } = trpc.getDefaultContactsByRequest.useQuery(
    { id: request?.id, updates_for: NotificationCategory.Quote },
    { enabled: !!request?.id },
  );
  const recipients = methods.watch('recipients');
  const defaultSelectedContacts = uniqBy([...(contacts || []), ...(defaultContacts || [])].filter(Boolean), 'id');

  const { sendNewRateEmail, isLoading } = useEmail();

  const { getIsModalOpen, closeModal } = useRequestDrawersContext();
  const isOpen = getIsModalOpen('NewRate');
  const onClose = () => closeModal('NewRate');

  const bookedQuote = request?.booked_quote_variant?.quote;
  const origin = bookedQuote?.origin_airport_id ?? request?.origin_airports?.[0]?.id;
  const destination =
    bookedQuote?.destination_airport_id ??
    request?.destination_airports?.[request?.destination_airports?.length - 1]?.id;

  const customMessage = `Please note a new deal has been added to R${request?.id} from ${origin} to ${destination}.`;
  const defaultRequest = {
    ...request,
    customMessage,
  };

  const handleSendEmail = async (overrides: any) => {
    try {
      const requestId = request?.id;
      if (!requestId) return;

      const { senderContactId } = overrides;

      const toContact = recipients?.[0];

      const ccContacts = recipients?.slice(1);

      await sendNewRateEmail({
        requestId,
        senderContactId,
        toContact,
        ccContacts,
        overrides,
      });

      toast({
        title: 'Success',
        description: overrides?.clipboard ? 'Email copied to clipboard' : 'Email sent successfully',
        status: 'success',
      });
    } catch (error) {
      toast({ title: 'Error', description: error?.message, status: 'error' });
    }
  };

  const canSend = !!recipients?.length;

  const showWarning = useMemo(() => {
    if (!recipients?.length) return false;

    return hasCustomerEmail(recipients);
  }, [recipients]);

  return (
    <SimpleForm {...methods} submitOnBlur={false}>
      <EmailDrawer isOpen={isOpen} onClose={onClose}>
        <EmailDrawerLeftPanel
          title="Specify contact(s)"
          description="All In Progress quotes on this Request will be included in the email."
        >
          <SimpleCustomerRecipientForm
            name="recipients"
            defaultAccount={request?.account}
            defaultSelectedContacts={defaultSelectedContacts}
            searchPattern="FF"
          />
        </EmailDrawerLeftPanel>
        <EmailDrawerRightPanel title="Send New Rate">
          <EmailQuoteForm
            sendLongerEmail
            request={defaultRequest}
            onSubmit={handleSendEmail}
            onClose={onClose}
            isLoading={isLoading}
            canSend={canSend}
            hasCustomerEmail={showWarning}
          />
        </EmailDrawerRightPanel>
      </EmailDrawer>
    </SimpleForm>
  );
};
